import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  MapPin, Square, ArrowRight, Minus, Pencil, Type, Ruler, Trash2,
  Loader2, Palette, Eye, EyeOff, Undo2, Save, X, ChevronDown, ChevronRight, MousePointer
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_CONFIG = {
  select: { label: 'Select', icon: MousePointer, cursor: 'default' },
  pin: { label: 'Pin', icon: MapPin, cursor: 'crosshair' },
  rectangle: { label: 'Rectangle', icon: Square, cursor: 'crosshair' },
  arrow: { label: 'Arrow', icon: ArrowRight, cursor: 'crosshair' },
  line: { label: 'Line', icon: Minus, cursor: 'crosshair' },
  freehand: { label: 'Freehand', icon: Pencil, cursor: 'crosshair' },
  text: { label: 'Text', icon: Type, cursor: 'text' },
  measurement: { label: 'Measure', icon: Ruler, cursor: 'crosshair' },
};

const COLORS = ['#1D4053', '#ef4444', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#0D1830', '#999999'];

export default function AnnotationTool({ projectId, fileId, pageNumber, containerRef, pdfWidth, pdfHeight }) {
  const [annotations, setAnnotations] = useState([]);
  const [activeTool, setActiveTool] = useState('select');
  const [activeColor, setActiveColor] = useState('#1D4053');
  const [drawing, setDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentPoint, setCurrentPoint] = useState(null);
  const [freehandPath, setFreehandPath] = useState([]);
  const [textInput, setTextInput] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const [textPos, setTextPos] = useState(null);
  const [selectedAnn, setSelectedAnn] = useState(null);
  const [showPanel, setShowPanel] = useState(true);
  const [scaleFactor, setScaleFactor] = useState(10); // px per foot
  const canvasRef = useRef(null);

  const fetchAnnotations = useCallback(async () => {
    try {
      let url = `${API}/annotations/${projectId}?file_id=${fileId}`;
      if (pageNumber) url += `&page_number=${pageNumber}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) setAnnotations(await res.json());
    } catch (e) {}
  }, [projectId, fileId, pageNumber]);

  useEffect(() => { fetchAnnotations(); }, [fetchAnnotations]);

  const getRelativePos = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleMouseDown = (e) => {
    if (activeTool === 'select') return;
    const pos = getRelativePos(e);
    setStartPoint(pos);
    setDrawing(true);
    if (activeTool === 'freehand') setFreehandPath([pos]);
    if (activeTool === 'text') {
      setTextPos(pos);
      setShowTextInput(true);
      setDrawing(false);
    }
    if (activeTool === 'pin') {
      saveAnnotation({ x: pos.x, y: pos.y, annotation_type: 'pin', text: '' });
      setDrawing(false);
    }
  };

  const handleMouseMove = (e) => {
    if (!drawing) return;
    const pos = getRelativePos(e);
    setCurrentPoint(pos);
    if (activeTool === 'freehand') setFreehandPath(prev => [...prev, pos]);
  };

  const handleMouseUp = () => {
    if (!drawing || !startPoint) return;
    setDrawing(false);
    const end = currentPoint || startPoint;
    
    if (activeTool === 'rectangle') {
      const x = Math.min(startPoint.x, end.x);
      const y = Math.min(startPoint.y, end.y);
      const w = Math.abs(end.x - startPoint.x);
      const h = Math.abs(end.y - startPoint.y);
      if (w > 5 && h > 5) saveAnnotation({ x, y, width: w, height: h, annotation_type: 'rectangle' });
    } else if (activeTool === 'arrow' || activeTool === 'line') {
      saveAnnotation({ x: startPoint.x, y: startPoint.y, x2: end.x, y2: end.y, annotation_type: activeTool });
    } else if (activeTool === 'freehand' && freehandPath.length > 2) {
      saveAnnotation({ x: freehandPath[0].x, y: freehandPath[0].y, path_points: freehandPath, annotation_type: 'freehand' });
    } else if (activeTool === 'measurement') {
      const dx = end.x - startPoint.x;
      const dy = end.y - startPoint.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const measuredValue = dist / scaleFactor;
      saveAnnotation({ x: startPoint.x, y: startPoint.y, x2: end.x, y2: end.y, annotation_type: 'measurement', measurement_value: Math.round(measuredValue * 10) / 10, measurement_unit: 'ft', scale_factor: scaleFactor });
    }
    setStartPoint(null);
    setCurrentPoint(null);
    setFreehandPath([]);
  };

  const saveAnnotation = async (data) => {
    try {
      const body = { project_id: projectId, file_id: fileId, page_number: pageNumber || 1, color: activeColor, ...data };
      const res = await fetch(`${API}/annotations`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(body)
      });
      if (res.ok) { const ann = await res.json(); setAnnotations(prev => [ann, ...prev]); }
    } catch (e) {}
  };

  const saveTextAnnotation = () => {
    if (!textInput.trim() || !textPos) return;
    saveAnnotation({ x: textPos.x, y: textPos.y, text: textInput, annotation_type: 'text' });
    setTextInput('');
    setShowTextInput(false);
    setTextPos(null);
  };

  const deleteAnnotation = async (annId) => {
    try {
      await fetch(`${API}/annotations/${annId}`, { method: 'DELETE', credentials: 'include' });
      setAnnotations(prev => prev.filter(a => a.annotation_id !== annId));
      if (selectedAnn?.annotation_id === annId) setSelectedAnn(null);
    } catch (e) {}
  };

  const renderAnnotation = (ann) => {
    const color = ann.color || '#1D4053';
    const isSelected = selectedAnn?.annotation_id === ann.annotation_id;
    const selStyle = isSelected ? { filter: 'brightness(1.2)', strokeWidth: 2 } : {};
    const common = { cursor: 'pointer', onClick: (e) => { e.stopPropagation(); setSelectedAnn(ann); setActiveTool('select'); } };

    switch (ann.annotation_type) {
      case 'pin':
        return <g key={ann.annotation_id} {...common}><circle cx={ann.x} cy={ann.y} r={6} fill={color} stroke="white" strokeWidth={2} {...selStyle} />{ann.text && <text x={ann.x + 10} y={ann.y + 4} fill={color} fontSize={10} fontWeight="bold">{ann.text}</text>}</g>;
      case 'rectangle':
        return <g key={ann.annotation_id} {...common}><rect x={ann.x} y={ann.y} width={ann.width || 0} height={ann.height || 0} fill={`${color}15`} stroke={color} strokeWidth={isSelected ? 2.5 : 1.5} rx={2} {...selStyle} /></g>;
      case 'arrow': {
        const dx = (ann.x2 || ann.x) - ann.x, dy = (ann.y2 || ann.y) - ann.y;
        const angle = Math.atan2(dy, dx);
        const tipSize = 10;
        const tipX1 = (ann.x2 || ann.x) - tipSize * Math.cos(angle - 0.4);
        const tipY1 = (ann.y2 || ann.y) - tipSize * Math.sin(angle - 0.4);
        const tipX2 = (ann.x2 || ann.x) - tipSize * Math.cos(angle + 0.4);
        const tipY2 = (ann.y2 || ann.y) - tipSize * Math.sin(angle + 0.4);
        return <g key={ann.annotation_id} {...common}><line x1={ann.x} y1={ann.y} x2={ann.x2} y2={ann.y2} stroke={color} strokeWidth={isSelected ? 2.5 : 2} /><polygon points={`${ann.x2},${ann.y2} ${tipX1},${tipY1} ${tipX2},${tipY2}`} fill={color} /></g>;
      }
      case 'line':
        return <line key={ann.annotation_id} x1={ann.x} y1={ann.y} x2={ann.x2} y2={ann.y2} stroke={color} strokeWidth={isSelected ? 2.5 : 2} {...common} />;
      case 'freehand': {
        const pts = ann.path_points || [];
        if (pts.length < 2) return null;
        const d = `M${pts[0].x},${pts[0].y} ${pts.slice(1).map(p => `L${p.x},${p.y}`).join(' ')}`;
        return <path key={ann.annotation_id} d={d} fill="none" stroke={color} strokeWidth={isSelected ? 3 : 2} strokeLinecap="round" {...common} />;
      }
      case 'text':
        return <g key={ann.annotation_id} {...common}><rect x={ann.x - 2} y={ann.y - 12} width={(ann.text?.length || 3) * 7 + 8} height={18} rx={3} fill="white" stroke={color} strokeWidth={1} opacity={0.9} /><text x={ann.x + 2} y={ann.y} fill={color} fontSize={11} fontWeight={500}>{ann.text}</text></g>;
      case 'measurement': {
        const midX = (ann.x + (ann.x2 || ann.x)) / 2, midY = (ann.y + (ann.y2 || ann.y)) / 2;
        const label = `${ann.measurement_value || 0} ${ann.measurement_unit || 'ft'}`;
        return <g key={ann.annotation_id} {...common}>
          <line x1={ann.x} y1={ann.y} x2={ann.x2} y2={ann.y2} stroke={color} strokeWidth={isSelected ? 2.5 : 1.5} strokeDasharray="6,3" />
          <circle cx={ann.x} cy={ann.y} r={3} fill={color} /><circle cx={ann.x2} cy={ann.y2} r={3} fill={color} />
          <rect x={midX - 25} y={midY - 10} width={50} height={16} rx={3} fill="white" stroke={color} strokeWidth={1} />
          <text x={midX} y={midY + 2} fill={color} fontSize={9} fontWeight="bold" textAnchor="middle">{label}</text>
        </g>;
      }
      default: return null;
    }
  };

  const renderPreview = () => {
    if (!drawing || !startPoint) return null;
    const end = currentPoint || startPoint;
    const color = activeColor;
    if (activeTool === 'rectangle') {
      const x = Math.min(startPoint.x, end.x), y = Math.min(startPoint.y, end.y);
      return <rect x={x} y={y} width={Math.abs(end.x - startPoint.x)} height={Math.abs(end.y - startPoint.y)} fill={`${color}15`} stroke={color} strokeWidth={1.5} strokeDasharray="4,2" rx={2} />;
    }
    if (activeTool === 'arrow' || activeTool === 'line' || activeTool === 'measurement') {
      return <line x1={startPoint.x} y1={startPoint.y} x2={end.x} y2={end.y} stroke={color} strokeWidth={1.5} strokeDasharray={activeTool === 'measurement' ? '6,3' : 'none'} />;
    }
    if (activeTool === 'freehand' && freehandPath.length > 1) {
      const d = `M${freehandPath[0].x},${freehandPath[0].y} ${freehandPath.slice(1).map(p => `L${p.x},${p.y}`).join(' ')}`;
      return <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" />;
    }
    return null;
  };

  const typeCounts = {};
  annotations.forEach(a => { const t = a.annotation_type || 'pin'; typeCounts[t] = (typeCounts[t] || 0) + 1; });

  return (
    <div className="relative" data-testid="annotation-tool">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-1.5 bg-white rounded-xl border border-[#E2E8ED] shadow-sm mb-2 flex-wrap">
        {Object.entries(TOOL_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <button key={key} onClick={() => setActiveTool(key)}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium transition-colors ${
                activeTool === key ? 'bg-[#0D1830] text-white' : 'text-[#446E87] hover:bg-[#F0F4F7]'
              }`} data-testid={`tool-${key}`}>
              <Icon className="w-3 h-3" />{cfg.label}
            </button>
          );
        })}
        <div className="w-px h-5 bg-[#EEE] mx-1" />
        {COLORS.map(c => (
          <button key={c} onClick={() => setActiveColor(c)}
            className={`w-5 h-5 rounded-full border-2 transition-all ${activeColor === c ? 'border-[#0D1830] scale-110' : 'border-transparent hover:border-[#E2E8ED]'}`}
            style={{ backgroundColor: c }} data-testid={`color-${c}`} />
        ))}
        <div className="w-px h-5 bg-[#EEE] mx-1" />
        {activeTool === 'measurement' && (
          <div className="flex items-center gap-1">
            <span className="text-[9px] text-[#72A4BF]">Scale:</span>
            <Input type="number" value={scaleFactor} onChange={(e) => setScaleFactor(parseFloat(e.target.value) || 10)}
              className="w-12 h-5 text-[9px] px-1" /> <span className="text-[9px] text-[#72A4BF]">px/ft</span>
          </div>
        )}
        <button onClick={() => setShowPanel(!showPanel)} className="ml-auto p-1 rounded hover:bg-[#F0F4F7]" data-testid="toggle-panel">
          {showPanel ? <EyeOff className="w-3 h-3 text-[#72A4BF]" /> : <Eye className="w-3 h-3 text-[#72A4BF]" />}
        </button>
      </div>

      <div className="flex gap-2">
        {/* SVG Canvas Overlay */}
        <div className="flex-1 relative">
          <svg
            ref={canvasRef}
            className="absolute inset-0 z-10"
            width="100%" height="100%"
            style={{ cursor: TOOL_CONFIG[activeTool]?.cursor || 'default' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            data-testid="annotation-canvas"
          >
            {annotations.map(renderAnnotation)}
            {renderPreview()}
          </svg>
          {/* Text input popup */}
          {showTextInput && textPos && (
            <div className="absolute z-20 bg-white border border-[#1D4053] rounded-lg shadow-lg p-2" style={{ left: textPos.x, top: textPos.y }}>
              <Input value={textInput} onChange={(e) => setTextInput(e.target.value)} placeholder="Type text..." autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') saveTextAnnotation(); if (e.key === 'Escape') { setShowTextInput(false); setTextInput(''); } }}
                className="h-6 text-xs w-40 mb-1" data-testid="text-input" />
              <div className="flex gap-1">
                <Button size="sm" onClick={saveTextAnnotation} className="h-5 text-[9px] px-2 bg-[#0D1830] text-white rounded">Add</Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowTextInput(false); setTextInput(''); }} className="h-5 text-[9px] px-2"><X className="w-2.5 h-2.5" /></Button>
              </div>
            </div>
          )}
        </div>

        {/* Annotation Panel */}
        {showPanel && (
          <div className="w-56 bg-white border border-[#E2E8ED] rounded-lg overflow-hidden flex-shrink-0" data-testid="annotation-panel">
            <div className="px-3 py-2 bg-[#F7FAFB] border-b border-[#E2E8ED]">
              <span className="text-xs font-semibold text-[#0D1830]">Annotations ({annotations.length})</span>
            </div>
            <div className="p-2 border-b border-[#E2E8ED]">
              <div className="flex gap-1 flex-wrap">
                {Object.entries(typeCounts).map(([t, c]) => (
                  <Badge key={t} className="bg-[#F0F4F7] text-[#446E87] border-0 text-[8px] px-1.5 py-0 rounded-full capitalize">{t}: {c}</Badge>
                ))}
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {annotations.map(ann => (
                <div key={ann.annotation_id}
                  className={`px-3 py-2 border-b border-[#F0F4F7] hover:bg-[#F7FAFB] cursor-pointer transition-colors ${selectedAnn?.annotation_id === ann.annotation_id ? 'bg-[#72A4BF]/10' : ''}`}
                  onClick={() => setSelectedAnn(ann)}
                  data-testid={`ann-item-${ann.annotation_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ann.color || '#1D4053' }} />
                      <span className="text-[10px] font-medium capitalize">{ann.annotation_type || 'pin'}</span>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); deleteAnnotation(ann.annotation_id); }}
                      className="p-0.5 rounded hover:bg-red-50" data-testid={`delete-ann-${ann.annotation_id}`}><Trash2 className="w-2.5 h-2.5 text-red-400" /></button>
                  </div>
                  {ann.text && <p className="text-[9px] text-[#446E87] mt-0.5 truncate">{ann.text}</p>}
                  {ann.measurement_value && <p className="text-[9px] text-[#0D1830] font-medium mt-0.5">{ann.measurement_value} {ann.measurement_unit}</p>}
                  <p className="text-[8px] text-[#72A4BF]/60">{ann.author} · {ann.created_at ? new Date(ann.created_at).toLocaleTimeString() : ''}</p>
                </div>
              ))}
              {annotations.length === 0 && <p className="text-[10px] text-[#72A4BF]/60 text-center py-4">No annotations on this page</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
